import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { initiateOAuthLogin, exchangeCodeForToken, verifyState } from '../../services/oauthFlow';
import { resolveOAuthRedirectUri } from '../../utils/oauthRedirectUri';
import { privateApiClient, publicApiClient } from '../../services/apiClient';
import { storageKeys } from '../../config/environment';
import { extractPhoneNumber } from '../../services/authService';
import OrganizationSelection from './OrganizationSelection';
import OrganizationCreation from './OrganizationCreation';
import { normalizeCompanies, normalizeRoleKey } from '../../utils/organizationRoleUtils';
import { toEmailString } from '../../utils/toEmailString';

const Login = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(false);
    const [isProcessingCallback, setIsProcessingCallback] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [step, setStep] = useState('login');
    const [userData, setUserData] = useState(null);

    // Gérer le callback OAuth si un paramètre 'code' est présent dans l'URL
    useEffect(() => {
        const handleOAuthCallback = async () => {
            const searchParams = new URLSearchParams(location.search);
            const code = searchParams.get('code');
            const state = searchParams.get('state');
            const errorParam = searchParams.get('error');

            // Vérifier si l'utilisateur est déjà authentifié (même sans code)
            const savedSession = localStorage.getItem(storageKeys.session);
            if (savedSession) {
                try {
                    const session = JSON.parse(savedSession);
                    // Vérifier si la session n'est pas expirée
                    if (session.expires_at && session.expires_at > Date.now()) {
                        const savedOrg = localStorage.getItem(storageKeys.organisation);
                        if (savedOrg) {
                            // Si on a un code mais qu'on est déjà authentifié, nettoyer l'URL et rediriger
                            if (code) {
                                window.history.replaceState({}, document.title, window.location.pathname);
                            }
                            const org = JSON.parse(savedOrg);
                            navigate(org.role === 'cashier' ? '/cashier' : '/dashboard');
                            return;
                        }
                    }
                } catch (e) {
                    // Erreur de parsing, continuer avec le traitement normal
                }
            }

            // Si pas de code, on affiche juste le formulaire de connexion
            if (!code) {
                return;
            }

            // Nettoyer l'URL immédiatement pour éviter les doubles traitements
            window.history.replaceState({}, document.title, window.location.pathname);

            // Vérifier si ce code a déjà été traité
            const processedCode = sessionStorage.getItem(`oauth_code_processed_${code}`);
            if (processedCode === 'true') {
                console.warn('Code OAuth déjà traité, relance automatique de la connexion...');
                // Nettoyer les données de session pour forcer une nouvelle authentification
                sessionStorage.removeItem(`oauth_code_processed_${code}`);
                sessionStorage.removeItem('oauth_code_verifier');
                sessionStorage.removeItem('oauth_state');
                // Relancer automatiquement la connexion en arrière-plan
                try {
                    await initiateOAuthLogin();
                } catch (error) {
                    console.error('Erreur lors de la relance automatique:', error);
                    setErrorMessage('Erreur lors de la connexion. Veuillez réessayer.');
                    setStep('login');
                }
                return;
            }

            setIsProcessingCallback(true);
            setErrorMessage('');

            try {
                // Vérifier s'il y a une erreur
                if (errorParam) {
                    const errorDescription = searchParams.get('error_description') ||
                        'Erreur lors de l\'authentification';
                    throw new Error(errorDescription);
                }

                // Vérifier le state (sécurité CSRF)
                if (!verifyState(state)) {
                    throw new Error('État de sécurité invalide. Veuillez réessayer.');
                }

                const redirectUri = resolveOAuthRedirectUri();

                // Échanger le code contre un token
                await exchangeCodeForToken(code, redirectUri);

                // Marquer le code comme traité uniquement après un échange réussi
                sessionStorage.setItem(`oauth_code_processed_${code}`, 'true');

                // Récupérer la solution POS
                const solutionsResponse = await publicApiClient.get('/usr/solutions', {
                    params: { code: 'POS' }
                });
                const solution = solutionsResponse?.data;
                if (!solution?.id) {
                    throw new Error('Solution POS introuvable pour ce compte.');
                }

                // Récupérer les organisations de l'utilisateur
                const companiesResponse = await privateApiClient.get('/usr/companies', {
                    params: {
                        cat: 'all',
                        sol: solution.id,
                        slug: '',
                        pge: 1,
                        lmt: 100
                    }
                });

                const companies = companiesResponse?.data?.contents || [];

                // Récupérer les informations de session utilisateur
                const userSessionResponse = await privateApiClient.get('/usr/users/session');
                const profile = userSessionResponse?.data?.contents || userSessionResponse?.data || {};

                const normalizedCompanies = normalizeCompanies(companies);

                // Stocker les données
                localStorage.setItem(storageKeys.solution, solution.id);
                localStorage.setItem(storageKeys.organizations, JSON.stringify(normalizedCompanies));

                // Extraire le numéro de téléphone si c'est un objet
                const phoneNumber = extractPhoneNumber(profile.phone);

                const user = {
                    id: profile.id,
                    username: profile.username || phoneNumber || '',
                    name: profile.fullName || profile.name || profile.username || phoneNumber || '',
                    email: toEmailString(profile.email)
                };

                localStorage.setItem(storageKeys.user, JSON.stringify(user));

                // Préparer les données utilisateur pour l'affichage
                setUserData({
                    ...user,
                    organizations: normalizedCompanies
                });

                if (normalizedCompanies.length === 1) {
                    const org = normalizedCompanies[0];
                    if (org.role === 'both') {
                        setUserData({
                            ...user,
                            organizations: normalizedCompanies,
                        });
                        setStep('organization-select');
                    } else {
                        localStorage.setItem(storageKeys.organisation, JSON.stringify(org));
                        localStorage.setItem(storageKeys.destination, org.id);
                        window.location.href = org.role === 'cashier' ? '/cashier' : '/dashboard';
                    }
                } else if (normalizedCompanies.length > 0) {
                    // Sinon, afficher la sélection d'organisation
                    setStep('organization-select');
                } else {
                    // Aucune organisation, proposer la création
                    setStep('organization-create');
                }
            } catch (err) {
                console.error('Erreur lors du callback OAuth:', err);
                // Si c'est une erreur invalid_grant, c'est probablement un code déjà utilisé
                if (err.message && (err.message.includes('invalid_grant') || err.message.includes('Code d\'authentification'))) {
                    console.warn('Code OAuth invalide ou déjà utilisé, relance automatique de la connexion...');
                    // Nettoyer les données de session pour forcer une nouvelle authentification
                    localStorage.removeItem(storageKeys.session);
                    sessionStorage.removeItem(`oauth_code_processed_${code}`);
                    sessionStorage.removeItem('oauth_code_verifier');
                    sessionStorage.removeItem('oauth_state');
                    // Relancer automatiquement la connexion en arrière-plan
                    try {
                        await initiateOAuthLogin();
                        // Ne pas afficher d'erreur, la relance est en cours
                        return;
                    } catch (relaunchError) {
                        console.error('Erreur lors de la relance automatique:', relaunchError);
                        setErrorMessage('Erreur lors de la connexion. Veuillez réessayer.');
                        setStep('login');
                    }
                } else {
                    setErrorMessage(err.message || 'Une erreur est survenue lors de l\'authentification');
                }
            } finally {
                setIsProcessingCallback(false);
            }
        };

        handleOAuthCallback();
    }, [location.search, navigate]);

    const handleLogin = async () => {
        try {
            setIsLoading(true);
            setErrorMessage('');
            await initiateOAuthLogin();
            // La redirection se fera automatiquement vers le serveur OAuth
        } catch (error) {
            console.error('Erreur lors du lancement OAuth:', error);
            setErrorMessage(error.message || 'Erreur lors de la connexion. Veuillez réessayer.');
            setIsLoading(false);
        }
    }

    const handleOrganizationSelect = (organizationId, role, organizationOverride = null) => {
        // Utiliser l'organisation passée en paramètre ou chercher dans la liste
        let org = organizationOverride;
        if (!org) {
            org = userData?.organizations?.find(o => o.id === organizationId);
        }

        if (org) {
            const normalizedRole = normalizeRoleKey(role) || normalizeRoleKey(org.role) || 'admin';

            // Stocker aussi le rôle sélectionné pour les orgs "both"
            const orgWithSelectedRole = {
                ...org,
                selectedRole: normalizedRole,
            };

            // Mettre à jour l'organisation dans localStorage
            localStorage.setItem(storageKeys.organisation, JSON.stringify(orgWithSelectedRole));
            localStorage.setItem(storageKeys.destination, org.id);
            localStorage.setItem(storageKeys.selectedRole || 'netikash_selected_role', normalizedRole);

            // Déterminer la route cible selon le rôle
            const targetRoute = normalizedRole === 'cashier' ? '/cashier' : '/dashboard';

            // Forcer une navigation complète pour éviter les problèmes de cache et de contexte
            // Cela garantit que le contexte d'authentification se recharge correctement
            window.location.href = targetRoute;
        } else {
            console.error('Organisation non trouvée:', organizationId);
            setErrorMessage('Erreur lors de la sélection de l\'organisation');
        }
    }

    const handleOrganizationCreated = (organization) => {
        // Stocker la nouvelle organisation
        if (organization) {
            localStorage.setItem(storageKeys.organisation, JSON.stringify(organization));
            localStorage.setItem(storageKeys.destination, organization.id);
        }
        // Forcer une navigation complète
        window.location.href = '/dashboard';
    }

    const handleBackToLogin = () => {
        setStep('login');
        setUserData(null);
        // Nettoyer les données de session
        localStorage.removeItem(storageKeys.user);
        localStorage.removeItem(storageKeys.organizations);
        localStorage.removeItem(storageKeys.organisation);
        localStorage.removeItem(storageKeys.destination);
        localStorage.removeItem(storageKeys.session);
        // Nettoyer aussi sessionStorage pour éviter les problèmes de codes OAuth
        sessionStorage.clear();
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-4 relative overflow-hidden">
            {/* Lien retour vers l'accueil */}
            <Link
                to="/"
                className="absolute top-4 left-4 z-20 flex items-center gap-2 text-gray-400 hover:text-white transition-colors rounded-lg px-3 py-2 hover:bg-white/5"
                aria-label="Retour à l'accueil"
            >
                <ArrowLeft className="w-5 h-5" />
                <span className="text-sm font-medium">Retour</span>
            </Link>
            {/* Arrière-plan subtil avec texture */}
            <div className="absolute inset-0">
                <div className="absolute inset-0 bg-gradient-to-br from-amber-900/5 via-transparent to-amber-800/5"></div>
                <div className="absolute inset-0 opacity-[0.03]" style={{
                    backgroundImage: `radial-gradient(circle at 2px 2px, #fbbf24 1px, transparent 0)`,
                    backgroundSize: '40px 40px'
                }}></div>
            </div>

            {/* Effets lumineux subtils */}
            <div className="absolute top-1/4 -left-48 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl"></div>
            <div className="absolute bottom-1/4 -right-48 w-96 h-96 bg-amber-400/5 rounded-full blur-3xl"></div>

            {/* Conteneur principal */}
            <div className="w-full max-w-6xl mx-auto relative z-10">
                {/* Écran de chargement */}
                {isProcessingCallback && (
                    <div className="flex flex-col items-center justify-center min-h-[60vh]">
                        <div className="relative mb-8">
                            <div className="w-24 h-24 bg-gradient-to-br from-amber-500/10 to-amber-600/10 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-lg border border-amber-500/20">
                                <div className="text-amber-400 font-bold text-3xl tracking-tight">FLOW</div>
                            </div>
                            <div className="absolute -inset-4 border border-amber-500/10 rounded-3xl animate-pulse"></div>
                        </div>
                        <p className="text-xl font-medium text-gray-300 mb-2">Chargement de votre session</p>
                        <p className="text-gray-400">Préparation de votre environnement...</p>
                    </div>
                )}

                {/* Message d'erreur minimal */}
                {errorMessage && !isProcessingCallback && (
                    <div className="max-w-md mx-auto mb-8 animate-fade-in">
                        <div className="bg-gradient-to-r from-amber-900/20 to-amber-800/10 backdrop-blur-sm rounded-xl p-6 border border-amber-700/30 shadow-lg">
                            <div className="flex items-start space-x-3">
                                <div className="flex-shrink-0 mt-1">
                                    <svg className="w-6 h-6 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <div className="flex-1">
                                    <p className="text-amber-100 mb-3">{errorMessage}</p>
                                    <button
                                        onClick={() => setErrorMessage('')}
                                        className="px-4 py-2 bg-amber-900/30 hover:bg-amber-900/40 border border-amber-700/30 text-amber-300 rounded-lg text-sm font-medium transition-colors"
                                    >
                                        Réessayer
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {step === 'login' && !isProcessingCallback && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 xl:gap-20 items-center">
                        {/* Section branding minimaliste */}
                        <div className="space-y-8 order-2 lg:order-1">
                            {/* Logo et marque */}
                            <div>
                                <div className="flex items-center space-x-4 mb-8">
                                    <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center shadow-lg ring-1 ring-amber-500/20 overflow-hidden">
                                        <img
                                            src={`${process.env.PUBLIC_URL}/images/Logo_Netikash Flow - Logo Only 512x512.png`}
                                            alt="Netikash Flow"
                                            className="h-9 w-9 object-contain drop-shadow-sm"
                                            loading="eager"
                                        />
                                    </div>
                                    <div>
                                        <h1 className="text-4xl font-bold text-white tracking-tight">
                                            FLOW<span className="text-amber-400">POS</span>
                                        </h1>
                                        <p className="text-gray-400 mt-1">by Netikash</p>
                                    </div>
                                </div>

                                <h2 className="text-3xl lg:text-4xl font-light text-gray-200 leading-snug">
                                    Système de paiement
                                    <span className="block font-semibold text-amber-300">
                                        moderne et intuitif
                                    </span>
                                </h2>
                            </div>

                            {/* Points clés épurés */}
                            <div className="space-y-6">
                                <div className="flex items-start space-x-4">
                                    <div className="flex-shrink-0 mt-1">
                                        <svg className="w-6 h-6 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="font-medium text-white mb-1">Transactions sécurisées</h3>
                                        <p className="text-gray-400 text-sm">Paiements cryptés en temps réel</p>
                                    </div>
                                </div>

                                <div className="flex items-start space-x-4">
                                    <div className="flex-shrink-0 mt-1">
                                        <svg className="w-6 h-6 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="font-medium text-white mb-1">Interface fluide</h3>
                                        <p className="text-gray-400 text-sm">Conçu pour une utilisation rapide</p>
                                    </div>
                                </div>

                                <div className="flex items-start space-x-4">
                                    <div className="flex-shrink-0 mt-1">
                                        <svg className="w-6 h-6 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="font-medium text-white mb-1">Analyses détaillées</h3>
                                        <p className="text-gray-400 text-sm">Données commerciales claires</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Carte de connexion épurée */}
                        <div className="relative order-1 lg:order-2">
                            <div className="bg-gray-900/50 backdrop-blur-md rounded-2xl shadow-2xl border border-gray-800/50 p-6 sm:p-8 max-w-md mx-auto w-full">
                                {/* En-tête minimal */}
                                <div className="text-center mb-10">
                                    <div className="w-12 h-12 bg-gradient-to-br from-amber-500/20 to-amber-600/20 backdrop-blur-sm rounded-xl flex items-center justify-center mx-auto mb-6 border border-amber-500/30">
                                        <svg className="w-6 h-6 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                                        </svg>
                                    </div>
                                    <h2 className="text-2xl font-semibold text-white mb-2">Accéder à votre compte</h2>
                                    <p className="text-gray-400">
                                        Connectez-vous pour gérer vos transactions
                                    </p>
                                </div>

                                {/* Bouton de connexion élégant */}
                                <button
                                    onClick={handleLogin}
                                    disabled={isLoading}
                                    className="w-full group relative bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-medium py-4 px-6 rounded-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl hover:shadow-amber-500/25 hover:scale-[1.02]"
                                >
                                    <div className="flex items-center justify-center space-x-3">
                                        {isLoading ? (
                                            <>
                                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                                <span>Connexion...</span>
                                            </>
                                        ) : (
                                            <>
                                                <span>Se connecter</span>
                                                <svg className="w-5 h-5 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                                </svg>
                                            </>
                                        )}
                                    </div>
                                </button>

                                {/* Séparateur minimal */}
                                <div className="relative my-8">
                                    <div className="absolute inset-0 flex items-center">
                                        <div className="w-full border-t border-gray-800"></div>
                                    </div>
                                    <div className="relative flex justify-center text-sm">
                                        <span className="px-4 bg-gray-900/50 text-gray-500">Accès sécurisé</span>
                                    </div>
                                </div>

                                {/* Indicateurs de sécurité discrets */}
                                <div className="flex items-center justify-center space-x-6 text-sm text-gray-400">
                                    <div className="flex items-center space-x-2">
                                        <svg className="w-4 h-4 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                        </svg>
                                        <span>SSL</span>
                                    </div>
                                    <div className="w-1 h-1 bg-gray-700 rounded-full"></div>
                                    <div className="flex items-center space-x-2">
                                        <svg className="w-4 h-4 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                        </svg>
                                        <span>Chiffré</span>
                                    </div>
                                </div>

                                {/* Version en bas */}
                                <div className="mt-8 pt-6 border-t border-gray-800/50">
                                    <div className="text-center text-xs text-gray-500">
                                        Version 3.2.1
                                    </div>
                                </div>
                            </div>

                            {/* Détail décoratif subtil */}
                            <div className="absolute -z-10 -top-4 -right-4 w-32 h-32 bg-gradient-to-br from-amber-500/5 to-amber-400/5 rounded-2xl backdrop-blur-sm"></div>
                        </div>
                    </div>
                )}

                {step === 'organization-select' && userData && (
                    <OrganizationSelection
                        user={userData}
                        onOrganizationSelect={handleOrganizationSelect}
                        onBack={handleBackToLogin}
                    />
                )}

                {step === 'organization-create' && (
                    <OrganizationCreation
                        onOrganizationCreated={handleOrganizationCreated}
                        onBack={handleBackToLogin}
                    />
                )}
            </div>

            {/* Footer minimal */}
            <div className="absolute bottom-6 left-0 right-0">
                <div className="text-center">
                    <div className="text-sm text-gray-500 space-y-1">
                        <div>© 2025 Netikash Flow</div>
                        <div className="flex items-center justify-center flex-wrap gap-x-3 gap-y-1 text-xs">
                            <Link to="/about" className="hover:text-amber-400 transition-colors">À propos</Link>
                            <span className="w-1 h-1 bg-gray-700 rounded-full hidden sm:inline-block"></span>
                            <Link to="/contact" className="hover:text-amber-400 transition-colors">Contact</Link>
                            <span className="w-1 h-1 bg-gray-700 rounded-full hidden sm:inline-block"></span>
                            <Link to="/faq" className="hover:text-amber-400 transition-colors">FAQ</Link>
                        </div>
                    </div>
                </div>
            </div>

            {/* Indicateur système discret */}
            <div className="absolute top-6 right-6">
                <div className="flex items-center space-x-2 bg-gray-900/50 backdrop-blur-sm rounded-full px-3 py-1.5 border border-gray-800/50">
                    <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></div>
                    <span className="text-xs text-gray-300 font-medium">Système actif</span>
                </div>
            </div>
        </div>
    )
}

export default Login